import * as React from "react";

import { cn } from "@/lib/cn";

/**
 * InfoField + InfoFieldGroup
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.6
 *
 * Label/value pair used inside `<Section>` bodies. The label is
 * `text-xs text-muted-foreground`, the value is `text-sm font-medium`.
 *
 * `<InfoField>` auto-hides when its value is null/undefined/empty string so
 * detail pages don't need to wrap each row in conditional rendering. Pass an
 * explicit `<InfoField value="—" />` if you want to render a hyphen for empty.
 *
 * Compose with `<InfoFieldGroup columns={2}>` for two-column grids — the
 * canonical detail-page layout uses 2 columns on `sm+`, falling back to a
 * single stack on mobile.
 */
export interface InfoFieldProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  label: React.ReactNode;
  value?: React.ReactNode;
  /** Optional muted helper text rendered under the value */
  hint?: React.ReactNode;
  /** Stable test id for QA selectors. */
  "data-testid"?: string;
}

export function InfoField({
  label,
  value,
  hint,
  className,
  "data-testid": dataTestId,
  ...rest
}: InfoFieldProps) {
  // Auto-hide on empty values (null/undefined/empty string only — `0` and
  // `false` are meaningful and must render).
  if (value === null || value === undefined || value === "") return null;

  return (
    <div
      data-testid={dataTestId}
      className={cn("flex flex-col gap-0.5", className)}
      {...rest}
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
      {hint ? (
        <span className="text-xs text-muted-foreground mt-0.5">{hint}</span>
      ) : null}
    </div>
  );
}

export interface InfoFieldGroupProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of columns from `sm+`. Mobile is always a single stack. */
  columns?: 1 | 2 | 3 | 4;
  /** Tailwind gap class, default `gap-4`. */
  gap?: string;
}

export function InfoFieldGroup({
  columns = 2,
  gap = "gap-4",
  className,
  children,
  ...rest
}: InfoFieldGroupProps) {
  const colsClass =
    columns === 1
      ? "grid-cols-1"
      : columns === 2
        ? "grid-cols-1 sm:grid-cols-2"
        : columns === 3
          ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
          : "grid-cols-1 sm:grid-cols-2 md:grid-cols-4";

  return (
    <div className={cn("grid", colsClass, gap, className)} {...rest}>
      {children}
    </div>
  );
}
